import { memo, useCallback, useEffect, useState } from 'react'

import { useLiveChatSDK } from '@inno-sdk/livechat-sdk'
import {
  CustomEvents,
  type LiveChatContact
} from 'shared/constants/customEvents'
import { useUserContext } from 'shared/hooks/useUserContext'
import {
  dispatchCustomEvent,
  dispatchShortcutActionFailed,
  dispatchShortcutActionReady,
  listenCustomEvent
} from 'shared/utils/customEvents'

import { CallWidget } from './CallWidget'
import { LiveChatBackgroundSync } from './LiveChatBackgroundSync'
import { LiveChatScreenRecorder } from './LiveChatScreenRecorder'
import { UserChatModal } from './UserChatModal'

export const LiveChatWidgets = memo(() => {
  const { user } = useUserContext()
  const { livechat: tinode } = useLiveChatSDK()
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [userChatRequest, setUserChatRequest] = useState<{
    topicId: string
    isOpen: boolean
    requestId?: string
    contact?: LiveChatContact
  } | null>(null)

  useEffect(
    () =>
      listenCustomEvent(
        CustomEvents.OPEN_LIVECHAT_USER_CHAT_MODAL,
        ({ topicId, requestId, contact }) => {
          if (topicId)
            setUserChatRequest({ topicId, isOpen: true, requestId, contact })
        }
      ),
    []
  )

  const handleUserChatReady = useCallback(() => {
    if (!userChatRequest?.requestId) return

    dispatchShortcutActionReady('openSupport', userChatRequest.requestId)
  }, [userChatRequest?.requestId])

  const handleUserChatError = useCallback(() => {
    if (!userChatRequest?.requestId) return

    dispatchShortcutActionFailed('openSupport', userChatRequest.requestId)
  }, [userChatRequest?.requestId])

  const closeUserChat = useCallback(() => {
    setUserChatRequest(request =>
      request ? { ...request, isOpen: false } : request
    )
  }, [])

  const isChatSessionReady = Boolean(isLoggedIn || tinode?.isAuthenticated?.())

  if (tinode)
    tinode.onLoginSuccessfulContainer = () => {
      setIsLoggedIn(true)
      dispatchCustomEvent(CustomEvents.LIVECHAT_SESSION_READY)
    }

  if (!user) return null

  return (
    <>
      <CallWidget tinode={tinode} isLoggedIn={isLoggedIn} />
      <LiveChatBackgroundSync tinode={tinode} isLoggedIn={isLoggedIn} />
      <LiveChatScreenRecorder />
      {isChatSessionReady && tinode && userChatRequest && (
        <UserChatModal
          livechat={tinode}
          topicId={userChatRequest.topicId}
          isOpen={userChatRequest.isOpen}
          contact={userChatRequest.contact}
          onClose={closeUserChat}
          requestId={userChatRequest.requestId}
          onReady={handleUserChatReady}
          onError={handleUserChatError}
        />
      )}
    </>
  )
})
